import { useEffect } from "react";
import { useJoyride, type Step } from "react-joyride";
import { useNavigate } from "react-router";
import type { TourStep } from "./useTourSteps";

interface SpotlightTourProps {
  steps: TourStep[];
  onFinish: () => void;
  onRequireSidebarExpanded: () => void;
}

const WELCOME_STEP: Step = {
  target: "body",
  placement: "center",
  title: "Bem-vindo ao Innsight",
  content: "Vamos te mostrar como reserva → hospedagem → conta → saída se conectam. 2 minutos, pode pular quando quiser.",
};

export function SpotlightTour({ steps, onFinish, onRequireSidebarExpanded }: SpotlightTourProps) {
  const navigate = useNavigate();

  const joyrideSteps: Step[] = [
    WELCOME_STEP,
    ...steps.map((step) => ({
      target: step.targetSelector,
      title: step.title,
      content: step.rule,
      before: () => {
        onRequireSidebarExpanded();
        navigate(step.path);
        return new Promise<void>((resolve) => setTimeout(resolve, 300));
      },
    })),
  ];

  const { on, Tour } = useJoyride({
    steps: joyrideSteps,
    continuous: true,
    // `run` default e false na v3 (confirmado em node_modules/react-joyride/dist/index.d.cts) —
    // sem isso o Tour monta mas nunca começa a exibir nada.
    run: true,
    // Rotulos em pt-BR — default da lib e ingles ("Back"/"Next"/"Last"/"Skip").
    // `nextWithProgress` e o rotulo real usado quando `showProgress` esta ligado.
    locale: {
      back: "Voltar",
      close: "Fechar",
      last: "Concluir",
      next: "Próximo",
      nextWithProgress: "Próximo ({current} de {total})",
      skip: "Pular",
    },
    // showProgress/closeButtonAction/cores sao Options da v3, nao Props de topo —
    // precisam ir dentro de `options` (checado contra node_modules/react-joyride/dist/index.d.cts).
    // As cores usam as variáveis CSS do design system (hsl(var(--x))) em vez de valores
    // fixos, pra herdar automaticamente o tema claro/escuro ativo — sem isso a tooltip
    // usa o branco padrão da lib, sem nada do visual do Innsight.
    options: {
      showProgress: true,
      // Sem prop dedicada de "skip" na v3: o botao de fechar (x) e configurado
      // para encerrar o tour inteiro em vez de só a tooltip atual — é o "Pular".
      closeButtonAction: "skip",
      primaryColor: "hsl(var(--primary))",
      backgroundColor: "hsl(var(--popover))",
      textColor: "hsl(var(--popover-foreground))",
      arrowColor: "hsl(var(--popover))",
      overlayColor: "rgba(0, 0, 0, 0.6)",
    },
    styles: {
      tooltipContainer: {
        borderRadius: "var(--radius)",
        border: "1px solid hsl(var(--border))",
        boxShadow: "0 20px 40px -8px rgba(0, 0, 0, 0.35)",
        fontFamily: "inherit",
      },
      tooltipTitle: {
        fontWeight: 700,
        fontSize: "1rem",
      },
      tooltipContent: {
        fontSize: "0.875rem",
        lineHeight: 1.6,
        color: "hsl(var(--muted-foreground))",
      },
      buttonPrimary: {
        borderRadius: "0.75rem",
        fontWeight: 600,
        padding: "0.5rem 1rem",
      },
      buttonBack: {
        color: "hsl(var(--muted-foreground))",
      },
      buttonSkip: {
        color: "hsl(var(--muted-foreground))",
      },
      buttonClose: {
        color: "hsl(var(--muted-foreground))",
      },
    },
  });

  // Roda uma única vez na montagem: o tour só monta quando shouldAutoStart vira
  // verdadeiro, então "expandir a sidebar" é uma ação pontual de início, não algo
  // a repetir a cada re-render (evita reentrância em efeitos de outros componentes).
  useEffect(() => {
    onRequireSidebarExpanded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = on("tour:end", () => {
      onFinish();
    });
    return unsubscribe;
  }, [on, onFinish]);

  return Tour;
}
